import {Observable, OperatorFunction, TeardownLogic, Subscriber, Operator} from "rxjs";
import {ApolloQueryResult, OperationVariables} from "apollo-client";
import {GqlError} from "../errors/gql-error";
import * as moment from "moment";
import {get} from "lodash";
import {JSONModel, jsonProperty} from "../json.model";
import {enumerable} from "../helpers/property-descriptor-decorators";
import gql from "graphql-tag";

export type GqlModelOperator<R> = OperatorFunction<ApolloQueryResult<any>, R>;
export type GqlModelListOperator<R> = OperatorFunction<ApolloQueryResult<any>, R[]>;

export interface GqlConstructor {
  new (data: any): GqlModel;
}

class ApolloBaseSubscriber<Z> extends Subscriber<ApolloQueryResult<any>> {
  constructor(destination: Subscriber<Z>, protected Clazz: GqlConstructor, protected key: string) {
    super(destination);
  }

  error(err: any) {
    if (!this.isStopped) {
      err = new GqlError(err);

      console.error(err);
      super.error(err);
    }
  }
}

function constructGqlModel(Clazz: GqlConstructor, data: any, index?: number) {
  if (Clazz["typeRegistry"]) {
    const type = data["__typename"];
    Clazz = Clazz["typeRegistry"][type];

    if (!Clazz) {
      throw new Error(`Unexpected type ${type}`);
    }
  }

  const result = new Clazz(data) as GqlModel;

  if (index !== undefined) {
    JSONModel.setIndex(result, index);
  }

  return result;
}

class ApolloMapSubscriber<R> extends ApolloBaseSubscriber<R> {
  // NOTE: This looks unoptimized, but it's actually purposefully NOT
  // using try/catch optimizations.
  protected _next(value: ApolloQueryResult<any>) {
    let result: any;
    try {
      result = constructGqlModel(this.Clazz, this.key ? get(value.data, this.key) : value.data);
    } catch (err) {
      this.destination.error(err);
      return;
    }
    this.destination.next(result);
  }
}

class ApolloMapListSubscriber<R> extends ApolloBaseSubscriber<R[]> {
  // NOTE: This looks unoptimized, but it's actually purposefully NOT
  // using try/catch optimizations.
  protected _next(value: ApolloQueryResult<any>) {
    let result: any;
    try {
      result = (this.key ? get(value.data, this.key) : value.data)
        .map((it, index) => constructGqlModel(this.Clazz, it, index));
    } catch (err) {
      this.destination.error(err);
      return;
    }
    this.destination.next(result);
  }
}

class ApolloMapOperator<R> implements Operator<ApolloQueryResult<any>, R> {
  constructor(private Clazz: GqlConstructor, private key: string) {}

  call(subscriber: Subscriber<R>, source: any): TeardownLogic {
    return source.subscribe(new ApolloMapSubscriber(subscriber, this.Clazz, this.key));
  }
}

class ApolloMapListOperator<R> implements Operator<ApolloQueryResult<any>, R[]> {
  constructor(private Clazz: GqlConstructor, private key: string) {}

  call(subscriber: Subscriber<R[]>, source: any): TeardownLogic {
    return source.subscribe(new ApolloMapListSubscriber(subscriber, this.Clazz, this.key));
  }
}

export type GqlPropertyProcessorFunction = (prop?: any, data?: any, instance?: GqlModel) => any;
export type GqlPropertyProcessor = string | GqlPropertyProcessorFunction;
export type GqlPropertyDescriptor = string | {key: string, action: GqlPropertyProcessor};

export interface GqlModelOptions {
  properties: GqlPropertyDescriptor[];
}

export class GqlModel extends JSONModel {
  static typeRegistry: {[key: string]: GqlConstructor};

  /**
   * To implement for example class "MyModel", do this:
   * static mapper: GqlModelOperator<GqlConstructor> = GqlModel.buildMapper(MyModel, "key");
   * static mapper: GqlModelListOperator<GqlConstructor> = GqlModel.buildListMapper(MyModel, "key");
   */
  static mapper: GqlModelOperator<any> | GqlModelListOperator<any>;

  static query: any;

  protected static buildMapper<R>(Clazz: GqlConstructor, key: string = ""): GqlModelOperator<R> {
    return (source: Observable<ApolloQueryResult<any>>): Observable<R> =>
      source.lift(new ApolloMapOperator<R>(Clazz, key));
  }

  protected static buildListMapper<R>(Clazz: GqlConstructor, key: string = ""): GqlModelListOperator<R> {
    return (source: Observable<ApolloQueryResult<any>>): Observable<R[]> =>
      source.lift(new ApolloMapListOperator<R>(Clazz, key));
  }

  static register(typeName: string, Clazz: GqlConstructor) {
    if (!this.typeRegistry) this.typeRegistry = {};
    this.typeRegistry[typeName] = Clazz;
  }

  constructor(data: any, options?: GqlModelOptions) {
    super(data);

    if (options && options.properties) {
      data = data || {};

      for (const property of options.properties) {
        let key: string;
        let action: GqlPropertyProcessor;

        if (typeof property === "string") {
          key = property;
        } else if (property && typeof property === "object") {
          key = property.key;
          action = property.action;
        }

        if (action === "date") {
          if (data[key] !== null && data[key] !== undefined) {
            const value = moment.utc(+data[key]);
            if (value.isValid()) {
              this[key] = value.toDate();
            } else {
              console.warn(`Date value ${data[key]} is not valid.`);
            }
          }
        } else if (action === "json") {
          if (data[key] === undefined || data[key] === "undefined") {
            this[key] = undefined;
          } else {
            try {
              this[key] = JSON.parse(data[key]);
            } catch (error) {
              console.warn(error);
            }
          }
        } else if (typeof action === "function") {
          this[key] = action(data[key], data, this);
        } else {
          this[key] = data[key];
        }
      }
    }
  }
}

export class GqlQueryModel extends GqlModel {
  static query: any;
  static variables: (...args) => OperationVariables = () => ({});
}

export class GqlMutationModel extends GqlModel {
  @enumerable(false)
  get mutation(): any {
    return (this.constructor as typeof GqlMutationModel).query;
  }

  @enumerable(false)
  get variables(): OperationVariables {
    return this as OperationVariables;
  }
}

export interface GqlModelFactory<T extends GqlModel> {
  build(...args: any[]): T;
}

export class GqlMicroFormQueryModel extends GqlQueryModel {
  @jsonProperty("string") paramName: string;
  @jsonProperty("json") paramValue: any;

  static fragment: any = gql`
    fragment microFormData on MicroFormResult {
      paramName
      paramValue
    }
  `;
}

export class GqlMicroFormMutationModel extends GqlMutationModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") paramName: string;
  @jsonProperty("JSON.stringify") paramValue: any;

  query: any;
  resultKey: string;

  get mapper() {
    return GqlModel.buildMapper(GqlMicroFormQueryModel, this.resultKey);
  }

  get mutation(): any {
    return this.query;
  }

  get variables(): OperationVariables {
    return {
      id: this.id,
      paramName: this.paramName,
      paramValue: this.paramValue,
    };
  }
}

export class GqlMicroFormMutationModelFactory implements GqlModelFactory<GqlMicroFormMutationModel> {
  constructor(
    private paramName: string,
    private getId: () => string,
    private query: any,
    private resultKey: string = "",
  ) {}

  build(paramValue: any): GqlMicroFormMutationModel {
    const model = new GqlMicroFormMutationModel({
      id: this.getId(),
      paramName: this.paramName,
      paramValue,
    });

    model.query = this.query;
    model.resultKey = this.resultKey;

    return model;
  }
}
